"use client";

import Image from "next/image";
import Link from "next/link";
import { useSearchParams } from 'next/navigation';
import VideoPlayer from "./components/VideoPlayer";

export default function Home() {
  const searchParams = useSearchParams();
  const disabledSection = searchParams?.get('test_disable') || null;

  return (
    <div className="min-h-screen flex flex-col">
      {/* Hero Section */}
      {disabledSection !== 'hero' && (
        <section className="relative flex flex-col items-center justify-center py-24 px-4 text-center bg-gradient-to-br from-[#1a2e4c] via-[#254063] to-[#1e4d45] text-white overflow-hidden">
          {/* Abstract shapes with optimized animations - matching technology-safety page */}
          <div className="absolute inset-0 opacity-20 pointer-events-none">
            <div className="absolute top-20 left-1/4 w-96 h-96 rounded-full bg-[#38b5a0] blur-3xl animate-pulse"></div>
            <div className="absolute bottom-10 right-1/4 w-80 h-80 rounded-full bg-[#f8c156] blur-3xl animate-pulse" style={{animationDelay: '1s'}}></div>
            <div className="absolute top-40 right-1/3 w-60 h-60 rounded-full bg-[#e05c88] blur-3xl animate-pulse" style={{animationDelay: '1.5s'}}></div>
            <div className="absolute bottom-20 left-1/5 w-72 h-72 rounded-full bg-[#2389db] blur-3xl animate-pulse" style={{animationDelay: '2s'}}></div>
          </div>
          
          <div className="max-w-5xl mx-auto relative z-10">
            <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight opacity-90">
              Elevate Your Fitness Business with <span className="bg-clip-text text-transparent bg-gradient-to-r from-[#38b5a0] to-[#2a8f7e]">I-motion EMS Technology</span>
            </h1>
            <div className="bg-gradient-to-r from-[#f8c156]/80 to-[#e67e22]/60 text-white text-lg md:text-xl py-3 px-6 rounded-full inline-block mb-8 border border-[#f8c156]/50 backdrop-blur-sm shadow-lg">
              Official UK Distributor
            </div>
            <p className="text-xl mb-10 max-w-3xl mx-auto">
              Experience the power of I-motion EMS technology - exclusively available in the UK through EMS Pulse. 
              Our advanced wireless EMS Suit systems are engineered for performance and aesthetics, delivering transformative EMS training in just 20 minutes.
            </p>

            {/* High-performance HTML5 video player with local file */}
            <VideoPlayer 
              sources={[
                { src: "/videos/ems-suit-video.webm", type: "video/webm" },
                { src: "/videos/ems-suit-video.mp4", type: "video/mp4" }
              ]}
              className="mx-auto mb-10 max-w-3xl" 
              poster="/images/placeholder.svg"
              aspectRatio="landscape"
              preload="metadata"
            />
            
            <div className="mt-8">
              <Link 
                href="/contact" 
                className="inline-flex items-center justify-center bg-primary text-white font-medium px-8 py-4 rounded-lg hover:bg-primary-hover transition-all shadow-xl shadow-primary/20 hover:translate-y-[-2px] text-lg"
              >
                <span>Request a Quote</span>
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-5 h-5 ml-2">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
                </svg>
              </Link>
            </div>
          </div>
          
          {/* Wave divider - optimized SVG */}
          <div className="absolute bottom-0 left-0 w-full overflow-hidden">
            <svg data-name="Layer 1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1200 120" preserveAspectRatio="none" className="w-full h-[60px]">
              <path d="M321.39,56.44c58-10.79,114.16-30.13,172-41.86,82.39-16.72,168.19-17.73,250.45-.39C823.78,31,906.67,72,985.66,92.83c70.05,18.48,146.53,26.09,214.34,3V0H0V27.35A600.21,600.21,0,0,0,321.39,56.44Z" className="fill-white"></path>
            </svg>
          </div>
        </section>
      )}

      {/* Official Distributor Section */}
      {disabledSection !== 'distributor' && (
        <section className="py-20 px-4 bg-gradient-to-b from-white to-[#F8F9FA]">
          <div className="max-w-5xl mx-auto">
            <div className="flex flex-col md:flex-row items-center gap-10">
              <div className="md:w-1/2">
                <div className="mb-6">
                  <span className="inline-block bg-[#38b5a0]/10 text-[#38b5a0] font-medium px-4 py-1 rounded-full text-sm mb-4">
                    Official Distributor
                  </span>
                  <h2 className="text-2xl md:text-4xl font-bold text-[#1a2942] relative">
                    <span className="inline-block bg-gradient-to-r from-[#f8c156] to-[#e67e22] h-1 w-16 absolute -top-4"></span>
                    The Official Source for I-motion EMS Technology in the UK
                  </h2>
                </div>
                <p className="text-lg text-[#455a73]">
                  As the exclusive UK distributor for I-motion, we provide fitness and wellness businesses with genuine, 
                  cutting-edge EMS Suit technology that delivers exceptional client results through revolutionary EMS training sessions in just 20 minutes.
                </p>
              </div>
              <div className="md:w-1/2 bg-card rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all relative overflow-hidden border border-border-medium">
                <div className="absolute top-0 right-0 w-32 h-32 bg-secondary/10 rounded-full blur-2xl"></div>
                <div className="absolute bottom-0 left-0 w-32 h-32 bg-primary/10 rounded-full blur-2xl"></div>
                <div className="relative z-10 space-y-6">
                  <div className="flex items-start gap-5">
                    <div className="bg-secondary rounded-xl p-3 text-white flex-shrink-0 shadow-md">
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-6 h-6">
                        <path fillRule="evenodd" d="M12.516 2.17a.75.75 0 0 0-1.032 0 11.209 11.209 0 0 1-7.877 3.08.75.75 0 0 0-.722.515A12.74 12.74 0 0 0 2.25 9.75c0 5.942 4.064 10.933 9.563 12.348a.749.749 0 0 0 .374 0c5.499-1.415 9.563-6.406 9.563-12.348 0-1.39-.223-2.73-.635-3.985a.75.75 0 0 0-.722-.516l-.143.001c-2.996 0-5.717-1.17-7.734-3.08Zm3.094 8.016a.75.75 0 1 0-1.22-.872l-3.236 4.53L9.53 12.22a.75.75 0 0 0-1.06 1.06l2.25 2.25a.75.75 0 0 0 1.14-.094l3.75-5.25Z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div>
                      <h3 className="font-bold text-xl mb-2 text-text-primary">Official Distributor Status</h3>
                      <p className="text-text-primary">Guaranteeing authentic I-motion EMS Suit systems</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-5">
                    <div className="bg-primary rounded-xl p-3 text-white flex-shrink-0 shadow-md">
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-6 h-6">
                        <path d="M18.375 2.25c-1.035 0-1.875.84-1.875 1.875v15.75c0 1.035.84 1.875 1.875 1.875h.75c1.035 0 1.875-.84 1.875-1.875V4.125c0-1.036-.84-1.875-1.875-1.875h-.75ZM9.75 8.625c0-1.036.84-1.875 1.875-1.875h.75c1.036 0 1.875.84 1.875 1.875v11.25c0 1.035-.84 1.875-1.875 1.875h-.75a1.875 1.875 0 0 1-1.875-1.875V8.625ZM3 13.125c0-1.036.84-1.875 1.875-1.875h.75c1.036 0 1.875.84 1.875 1.875v6.75c0 1.035-.84 1.875-1.875 1.875h-.75A1.875 1.875 0 0 1 3 19.875v-6.75Z" />
                      </svg>
                    </div>
                    <div>
                      <h3 className="font-bold text-xl mb-2 text-text-primary">20-Minute EMS Training</h3>
                      <p className="text-text-primary">Maximum results in minimum time</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Systems Section - optimized for better LCP */}
      {disabledSection !== 'systems' && (
        <section className="py-20 px-4 bg-page">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-2xl md:text-4xl font-bold mb-3 text-center text-text-primary">
              Two Specialized EMS Suit Systems for Distinct Business Goals
            </h2>
            <p className="text-center text-text-primary mb-16 max-w-3xl mx-auto text-lg">
              Choose the perfect EMS solution based on your business focus and client needs
            </p>
            
            <div className="grid md:grid-cols-2 gap-10">
              {/* Performance System */}
              <div className="relative bg-card rounded-2xl shadow-xl overflow-hidden transform transition-all hover:scale-[1.02] hover:shadow-2xl duration-300 border border-border-medium group">
                <div className="h-64 bg-gradient-to-br from-[#2389db]/90 to-[#165588]/95 flex items-center justify-center relative overflow-hidden">
                  {/* Subtle mesh gradient background */}
                  <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-[#2389db]/40 via-transparent to-transparent opacity-70"></div>
                  <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_left,_var(--tw-gradient-stops))] from-[#165588]/30 via-transparent to-transparent opacity-60"></div>
                  
                  {/* Product image */}
                  <div className="w-full h-full flex items-end justify-center relative z-[2]">
                    <Image 
                      src="/images/main-cards/i-motion-ems-suit.png"
                      alt="I-motion EMS System"
                      width={450}
                      height={250}
                      className="w-auto max-h-full object-contain object-bottom mix-blend-normal group-hover:scale-105 transition-transform duration-700"
                      priority
                      sizes="(max-width: 768px) 100vw, 450px"
                    />
                  </div>
                  
                  {/* Subtle animated accent elements */}
                  <div className="absolute top-8 right-8 w-32 h-32 rounded-full bg-white/5 blur-xl animate-pulse opacity-80" style={{animationDuration: '7s'}}></div>
                  <div className="absolute bottom-8 left-8 w-40 h-40 rounded-full bg-white/5 blur-xl animate-pulse opacity-60" style={{animationDuration: '10s', animationDelay: '1s'}}></div>
                  
                  {/* Bottom gradient overlay with text */}
                  <div className="absolute bottom-0 w-full z-[3] text-center px-4 py-3 bg-gradient-to-t from-[#165588] to-transparent">
                    <span className="inline-block px-4 py-1 mb-1 bg-white/10 backdrop-blur-sm rounded-md text-white text-sm font-medium border border-white/20 shadow-md">Performance Focus</span>
                    <h3 className="text-white text-2xl font-bold text-shadow">I-motion System</h3>
                  </div>
                </div>
                
                <div className="p-8">
                  <h3 className="text-2xl font-bold mb-6 text-text-primary text-center">Performance Excellence: I-motion EMS Suit</h3>
                  <div className="mb-8 space-y-5">
                    <div className="flex items-start gap-4">
                      <div className="bg-gradient-to-br from-imotion/20 to-imotion/10 p-2 rounded-lg text-imotion flex-shrink-0 border border-imotion/20 mt-0.5">
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-5 h-5">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
                        </svg>
                      </div>
                      <p className="text-text-primary">Engineered for <span className="font-semibold text-text-primary">athletic performance enhancement</span>, strength development, and recovery optimization</p>
                    </div>
                    <div className="flex items-start gap-4">
                      <div className="bg-gradient-to-br from-imotion/20 to-imotion/10 p-2 rounded-lg text-imotion flex-shrink-0 border border-imotion/20 mt-0.5">
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-5 h-5">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
                        </svg>
                      </div>
                      <p className="text-text-primary">Perfect EMS training solution for <span className="font-semibold text-text-primary">performance gyms, elite trainers</span>, and sports facilities</p>
                    </div>
                    <div className="flex items-start gap-4">
                      <div className="bg-gradient-to-br from-imotion/20 to-imotion/10 p-2 rounded-lg text-imotion flex-shrink-0 border border-imotion/20 mt-0.5">
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-5 h-5">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
                        </svg>
                      </div>
                      <p className="text-text-primary">Gives your business a <span className="font-semibold text-text-primary">competitive edge</span> with cutting-edge EMS technology</p>
                    </div>
                  </div>
                  <div className="flex justify-center">
                    <Link 
                      href="/i-motion-system" 
                      className="inline-flex items-center justify-center bg-gradient-to-r from-[#2389db] to-[#165588] text-white font-medium px-6 py-3 rounded-lg hover:shadow-xl transition-all space-x-2 min-w-48 shadow-lg hover:shadow-xl hover:translate-y-[-2px] group"
                    >
                      <span>Discover the I-motion System</span>
                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
                      </svg>
                    </Link>
                  </div>
                </div>
              </div>
              
              {/* Aesthetic System */}
              <div className="relative bg-card rounded-2xl shadow-xl overflow-hidden transform transition-all hover:scale-[1.02] hover:shadow-2xl duration-300 border border-border-medium group">
                <div className="h-64 bg-gradient-to-br from-[#e05c88]/70 to-[#c64276]/80 flex items-center justify-center relative overflow-hidden">
                  {/* Subtle mesh gradient background - matching style but different color */}
                  <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-[#e05c88]/30 via-transparent to-transparent opacity-60"></div>
                  <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_left,_var(--tw-gradient-stops))] from-[#c64276]/20 via-transparent to-transparent opacity-50"></div>
                  <div className="absolute inset-0 bg-white/5 backdrop-blur-[1px]"></div>
                  
                  {/* Product image */}
                  <div className="w-full h-full flex items-end justify-center relative z-[2]">
                    <Image 
                      src="/images/main-cards/i-model-ems-suit.png"
                      alt="i-model EMS System"
                      width={450}
                      height={250}
                      className="w-auto max-h-full object-contain object-bottom mix-blend-normal group-hover:scale-105 transition-transform duration-700"
                      priority
                      sizes="(max-width: 768px) 100vw, 450px"
                    />
                  </div>
                  
                  {/* Subtle animated accent elements - matching the first card but with different timing */}
                  <div className="absolute top-8 right-8 w-32 h-32 rounded-full bg-white/5 blur-xl animate-pulse opacity-70" style={{animationDuration: '8s'}}></div>
                  <div className="absolute bottom-8 left-8 w-40 h-40 rounded-full bg-white/5 blur-xl animate-pulse opacity-50" style={{animationDuration: '11s', animationDelay: '0.5s'}}></div>
                  
                  {/* Bottom gradient overlay with text */}
                  <div className="absolute bottom-0 w-full z-[3] text-center px-4 py-3 bg-gradient-to-t from-[#c64276]/90 to-transparent">
                    <span className="inline-block px-4 py-1 mb-1 bg-white/10 backdrop-blur-sm rounded-md text-white text-sm font-medium border border-white/20 shadow-md">Aesthetic Focus</span>
                    <h3 className="text-white text-2xl font-bold text-shadow">i-model System</h3>
                  </div>
                </div>
                
                <div className="p-8">
                  <h3 className="text-2xl font-bold mb-6 text-text-primary text-center">Aesthetic Transformation: i-model EMS Suit</h3>
                  <div className="mb-8 space-y-5">
                    <div className="flex items-start gap-4">
                      <div className="bg-gradient-to-br from-imodel/20 to-imodel/10 p-2 rounded-lg text-imodel flex-shrink-0 border border-imodel/20 mt-0.5">
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-5 h-5">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
                        </svg>
                      </div>
                      <p className="text-text-primary">Designed for <span className="font-semibold text-text-primary">body toning, definition enhancement</span>, and supporting fat loss goals</p>
                    </div>
                    <div className="flex items-start gap-4">
                      <div className="bg-gradient-to-br from-imodel/20 to-imodel/10 p-2 rounded-lg text-imodel flex-shrink-0 border border-imodel/20 mt-0.5">
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-5 h-5">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
                        </svg>
                      </div>
                      <p className="text-text-primary">Ideal for <span className="font-semibold text-text-primary">fitness studios, aesthetic clinics</span>, and personal trainers</p>
                    </div>
                    <div className="flex items-start gap-4">
                      <div className="bg-gradient-to-br from-imodel/20 to-imodel/10 p-2 rounded-lg text-imodel flex-shrink-0 border border-imodel/20 mt-0.5">
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-5 h-5">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
                        </svg>
                      </div>
                      <p className="text-text-primary">Focused on <span className="font-semibold text-text-primary">visible client transformations</span> through targeted EMS training</p>
                    </div>
                  </div>
                  <div className="flex justify-center">
                    <Link 
                      href="/i-model-system" 
                      className="inline-flex items-center justify-center bg-gradient-to-r from-[#e05c88] to-[#c64276] text-white font-medium px-6 py-3 rounded-lg hover:shadow-xl transition-all space-x-2 min-w-48 shadow-lg hover:shadow-xl hover:translate-y-[-2px] group"
                    >
                      <span>Explore the i-model System</span>
                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
                      </svg>
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Why Partner Section */}
      {disabledSection !== 'partner' && (
        <section className="py-20 px-4 bg-gradient-to-b from-[#F8F9FA] to-white relative overflow-hidden">
          <div className="absolute inset-0 opacity-10">
            <div className="absolute top-0 right-0 w-full h-full bg-[url('/pattern.svg')] bg-no-repeat bg-cover"></div>
          </div>
          
          <div className="max-w-5xl mx-auto relative z-10">
            <div className="text-center mb-16">
              <span className="inline-block bg-[#f8c156]/15 text-[#e67e22] font-medium px-4 py-1 rounded-full text-sm mb-4">
                Partnership Benefits
              </span>
              <h2 className="text-2xl md:text-4xl font-bold mb-3 text-[#1a2942]">
                Why Partner with EMS Pulse?
              </h2>
              <div className="h-1 w-24 bg-gradient-to-r from-[#38b5a0] to-[#2389db] mx-auto mb-5"></div>
              <p className="text-[#455a73] max-w-2xl mx-auto text-center">
                Join the exclusive network of official I-motion EMS Suit providers in the UK
              </p>
            </div>
            
            <div className="grid md:grid-cols-2 gap-8">
              <div className="bg-white rounded-xl border border-[#dee2e6] shadow-lg p-8 transition-all hover:translate-y-[-5px] hover:shadow-xl duration-300">
                <div className="flex flex-col items-center text-center">
                  <div className="bg-gradient-to-r from-[#38b5a0] to-[#2a8f7e] p-3 rounded-xl text-white mb-5 flex-shrink-0 shadow-md">
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-6 h-6">
                      <path fillRule="evenodd" d="M15.75 1.5a6.75 6.75 0 00-6.651 7.906c.067.39-.032.717-.221.906l-6.5 6.499a3 3 0 00-.878 2.121v2.818c0 .414.336.75.75.75H6a.75.75 0 00.75-.75v-1.5h1.5A.75.75 0 009 19.5V18h1.5a.75.75 0 00.53-.22l2.658-2.658c.19-.189.517-.288.906-.22A6.75 6.75 0 1015.75 1.5zm0 3a.75.75 0 000 1.5A2.25 2.25 0 0118 8.25a.75.75 0 001.5 0 3.75 3.75 0 00-3.75-3.75z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <h3 className="font-bold text-xl mb-3 text-[#1a2942]">Exclusive Official Partner</h3>
                  <p className="text-[#455a73]">
                    Direct access to genuine I-motion EMS Suit technology, ensuring quality, reliability, and authenticity for your business.
                  </p>
                </div>
              </div>
              
              <div className="bg-white rounded-xl border border-[#dee2e6] shadow-lg p-8 transition-all hover:translate-y-[-5px] hover:shadow-xl duration-300">
                <div className="flex flex-col items-center text-center">
                  <div className="bg-gradient-to-r from-[#2389db] to-[#165588] p-3 rounded-xl text-white mb-5 flex-shrink-0 shadow-md">
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-6 h-6">
                      <path fillRule="evenodd" d="M12 2.25c-5.385 0-9.75 4.365-9.75 9.75s4.365 9.75 9.75 9.75 9.75-4.365 9.75-9.75S17.385 2.25 12 2.25zM6.262 6.072a8.25 8.25 0 1010.562-.766 4.5 4.5 0 01-1.318 1.357L14.25 7.5l.165.33a.809.809 0 01-1.086 1.085l-.604-.302a1.125 1.125 0 00-1.298.21l-.132.131c-.439.44-.439 1.152 0 1.591l.296.296c.256.257.622.374.98.314l1.17-.195c.323-.054.654.036.905.245l1.33 1.108c.32.267.46.694.358 1.1a8.7 8.7 0 01-2.288 4.04l-.723.724a1.125 1.125 0 01-1.298.21l-.153-.076a1.125 1.125 0 01-.622-1.006v-1.089c0-.298-.119-.585-.33-.796l-1.347-1.347a1.125 1.125 0 01-.21-1.298L9.75 12l-1.64-1.64a6 6 0 01-1.676-3.257l-.172-1.03z" />
                    </svg>
                  </div>
                  <h3 className="font-bold text-xl mb-3 text-[#1a2942]">Premium Positioning</h3>
                  <p className="text-[#455a73]">
                    Attract high-value clientele and create new premium revenue streams with cutting-edge EMS Suit technology.
                  </p>
                </div>
              </div>
              
              <div className="bg-white rounded-xl border border-[#dee2e6] shadow-lg p-8 transition-all hover:translate-y-[-5px] hover:shadow-xl duration-300">
                <div className="flex flex-col items-center text-center">
                  <div className="bg-gradient-to-r from-[#f8c156] to-[#e67e22] p-3 rounded-xl text-white mb-5 flex-shrink-0 shadow-md">
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-6 h-6">
                      <path fillRule="evenodd" d="M8.25 6.75a3.75 3.75 0 117.5 0 3.75 3.75 0 01-7.5 0zM15.75 9.75a3 3 0 116 0 3 3 0 01-6 0zM2.25 9.75a3 3 0 116 0 3 3 0 01-6 0zM6.31 15.117A6.745 6.745 0 0112 12a6.745 6.745 0 016.709 7.498.75.75 0 01-.372.568A12.696 12.696 0 0112 21.75c-2.305 0-4.47-.612-6.337-1.684a.75.75 0 01-.372-.568 6.787 6.787 0 011.019-4.38z" />
                      <path d="M5.082 14.254a8.287 8.287 0 00-1.308 5.135 9.687 9.687 0 01-1.764-.44l-.115-.04a.563.563 0 01-.373-.487l-.01-.121a3.75 3.75 0 013.57-4.047zM20.226 19.389a8.287 8.287 0 00-1.308-5.135 3.75 3.75 0 013.57 4.047l-.01.121a.563.563 0 01-.373.486l-.115.04c-.567.2-1.156.349-1.764.441z" />
                    </svg>
                  </div>
                  <h3 className="font-bold text-xl mb-3 text-[#1a2942]">Complete UK Support</h3>
                  <p className="text-[#455a73]">
                    Expert installation, comprehensive EMS training programs, and ongoing local assistance for your business.
                  </p>
                </div>
              </div>

              <div className="bg-white rounded-xl border border-[#dee2e6] shadow-lg p-8 transition-all hover:translate-y-[-5px] hover:shadow-xl duration-300">
                <div className="flex flex-col items-center text-center">
                  <div className="bg-gradient-to-r from-[#e05c88] to-[#c64276] p-3 rounded-xl text-white mb-5 flex-shrink-0 shadow-md">
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-6 h-6">
                      <path fillRule="evenodd" d="M2.25 13.5a8.25 8.25 0 018.25-8.25.75.75 0 01.75.75v6.75H18a.75.75 0 01.75.75 8.25 8.25 0 01-16.5 0z" clipRule="evenodd" />
                      <path fillRule="evenodd" d="M12.75 3a.75.75 0 01.75-.75 8.25 8.25 0 018.25 8.25.75.75 0 01-.75.75h-7.5a.75.75 0 01-.75-.75V3z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <h3 className="font-bold text-xl mb-3 text-[#1a2942]">Proven EMS Training Results</h3>
                  <p className="text-[#455a73]">
                    Deliver exceptional client outcomes in just 20-minute EMS training sessions, dramatically improving efficiency and results.
                  </p>
                </div>
              </div>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center mt-12">
              <Link
                href="/why-ems-pulse"
                className="inline-block border border-border-medium bg-card text-text-primary font-medium px-6 py-4 rounded-lg hover:bg-page transition-colors text-center shadow-sm hover:shadow-md transition-all"
              >
                Learn About Our Partnership Approach
              </Link>
              <Link
                href="/contact"
                className="inline-block bg-gradient-to-r from-[#38b5a0] to-[#2a8f7e] text-white font-medium px-6 py-4 rounded-lg hover:bg-[#2a8f7e] transition-all shadow-lg shadow-[#38b5a0]/20 text-center hover:translate-y-[-2px]"
              >
                Request Your Quote Today
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* Info Callout Section - Note about finding right solution */}
      {disabledSection !== 'info' && (
        <section className="py-16 px-4 bg-card">
          <div className="max-w-5xl mx-auto">
            <div className="bg-[#2389db]/10 border-l-4 border-[#2389db] p-6 rounded-r-lg shadow-md">
              <h3 className="text-xl font-bold mb-3 text-text-primary">Not Sure Which EMS System Is Right for Your Business?</h3>
              <p className="text-text-primary mb-4">
                Our EMS specialists will help you determine the perfect solution based on your business focus, client base, and performance goals.
              </p>
              <Link href="/contact" className="inline-flex items-center justify-center bg-gradient-to-r from-[#2389db] to-[#165588] text-white font-medium px-6 py-3 rounded-lg hover:shadow-lg transition-all hover:translate-y-[-2px]">
                <span>Get Expert Advice</span>
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-4 h-4 ml-2">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
                </svg>
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* Call to Action */}
      {disabledSection !== 'cta' && (
        <section className="py-16 px-4 bg-gradient-to-b from-white to-[#F8F9FA]">
          <div className="max-w-5xl mx-auto">
            <div className="bg-gradient-to-br from-[#f8c156] to-[#e67e22] p-8 md:p-12 rounded-2xl shadow-xl border border-[#f8c156]/10">
              <div className="flex items-center gap-4 mb-6">
                <div className="bg-white rounded-full p-3 text-[#e67e22] shadow-lg">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                    className="w-8 h-8"
                  >
                    <path
                      fillRule="evenodd"
                      d="M12.516 2.17a.75.75 0 0 0-1.032 0 11.209 11.209 0 0 1-7.877 3.08.75.75 0 0 0-.722.515A12.74 12.74 0 0 0 2.25 9.75c0 5.942 4.064 10.933 9.563 12.348a.749.749 0 0 0 .374 0c5.499-1.415 9.563-6.406 9.563-12.348 0-1.39-.223-2.73-.635-3.985a.75.75 0 0 0-.722-.516l-.143.001c-2.996 0-5.717-1.17-7.734-3.08Zm3.094 8.016a.75.75 0 1 0-1.22-.872l-3.236 4.53L9.53 12.22a.75.75 0 0 0-1.06 1.06l2.25 2.25a.75.75 0 0 0 1.14-.094l3.75-5.25Z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
                <h2 className="text-2xl md:text-3xl font-bold text-white">
                  Ready to Transform Your Business?
                </h2>
              </div>

              <p className="text-lg mb-8 text-white/90">
                Join the exclusive network of official I-motion EMS providers in the UK. Our team is ready to help you get started with this revolutionary technology.
              </p>

              <div className="flex flex-col sm:flex-row gap-4">
                <Link
                  href="/contact"
                  className="inline-block bg-white text-[#e67e22] font-medium px-6 py-4 rounded-lg hover:bg-[#fff8f0] transition-all shadow-lg text-center hover:translate-y-[-2px]"
                >
                  Get Started Today
                </Link>
                <Link
                  href="/systems"
                  className="inline-block border-2 border-white bg-transparent text-white font-medium px-6 py-4 rounded-lg hover:bg-white/10 transition-colors text-center"
                >
                  Explore Our Systems
                </Link>
              </div>
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
